import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Sparkles, Eye, Mail, Phone, Code } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { CommunicationTemplate } from "@/types/communication-template";

interface CommunicationTemplateFormProps {
  template?: CommunicationTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TemplateVariable {
  variable_key: string;
  display_name: string;
  description?: string;
  sample_value: string;
  category: string;
}

export function CommunicationTemplateForm({ template, isOpen, onClose, onSuccess }: CommunicationTemplateFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    // Email fields
    email_subject_template: '',
    email_content_template: '',
    // SMS fields
    sms_content_template: '',
    // Webhook fields (standardized)
    webhook_url: '',
    webhook_secret_key: '',
    webhook_include_pii: true,
    // Deprecated webhook fields (backward compatibility)
    webhook_payload_template: '{}',
    webhook_headers: '{}',
    // Common fields
    variables: '{}',
    sample_data: '{}',
    is_active: true,
  });

  const [activeTab, setActiveTab] = useState<'email' | 'sms' | 'webhook'>('email');
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewDisplayMode, setPreviewDisplayMode] = useState<'desktop' | 'mobile'>('desktop');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [unknownVariables, setUnknownVariables] = useState<string[]>([]);
  
  // Multi-mode preview states
  const [previewDataMode, setPreviewDataMode] = useState<'sample' | 'booking'>('sample');
  const [selectedBookingId, setSelectedBookingId] = useState<string>('');

  const { toast } = useToast();

  // Fetch template variables from database
  const { data: templateVariables = [], isLoading: isLoadingVariables } = useQuery({
    queryKey: ['template-variables'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('template_variables')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('display_name', { ascending: true });
      
      if (error) throw error;
      return data as TemplateVariable[];
    },
  });

  // Fetch booking status categories for template categorization
  const { data: bookingCategories = [] } = useQuery({
    queryKey: ['booking-status-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('booking_statuses')
        .select('category')
        .eq('is_active', true)
        .not('category', 'is', null)
        .order('category', { ascending: true });
      
      if (error) throw error;
      
      // Extract unique categories
      const uniqueCategories = Array.from(new Set(data.map(item => item.category).filter(Boolean)));
      return uniqueCategories as string[];
    },
  });

  // Fetch recent bookings for preview testing
  const { data: recentBookings = [], isLoading: isLoadingBookings } = useQuery({
    queryKey: ['recent-bookings-preview'],
    queryFn: async () => {
      // First, get bookings that the user has access to (respects RLS)
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, status, created_at, user_id')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (bookingsError) {
        console.error('Error fetching bookings:', bookingsError);
        throw bookingsError;
      }
      
      if (!bookingsData || bookingsData.length === 0) {
        return [];
      }

      // Then fetch user profiles for those bookings
      const userIds = [...new Set(bookingsData.map(b => b.user_id).filter(Boolean))];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('auth_user_id, name')
        .in('auth_user_id', userIds);

      // Map profiles to bookings
      const profileMap = new Map(profilesData?.map(p => [p.auth_user_id, p.name]) || []);
      
      return bookingsData.map(booking => ({
        ...booking,
        customer_name: profileMap.get(booking.user_id) || 'Unknown Customer'
      }));
    },
    enabled: previewDataMode === 'booking',
    retry: 1
  });

  // Group variables by category
  const groupedVariables = templateVariables.reduce((acc, variable) => {
    if (!acc[variable.category]) {
      acc[variable.category] = [];
    }
    acc[variable.category].push(variable);
    return acc;
  }, {} as Record<string, TemplateVariable[]>);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        category: template.category,
        description: template.description || '',
        email_subject_template: template.email_subject_template || template.subject_template || '',
        email_content_template: template.email_content_template || template.content_template || '',
        sms_content_template: template.sms_content_template || '',
        webhook_url: template.webhook_url || '',
        webhook_secret_key: template.webhook_secret_key || '',
        webhook_include_pii: template.webhook_include_pii ?? true,
        webhook_payload_template: JSON.stringify(template.webhook_payload_template || {}, null, 2),
        webhook_headers: JSON.stringify(template.webhook_headers || {}, null, 2),
        variables: JSON.stringify(template.variables, null, 2),
        sample_data: JSON.stringify(template.sample_data, null, 2),
        is_active: template.is_active,
      });
    } else {
      setFormData({
        name: '',
        category: '',
        description: '',
        email_subject_template: '',
        email_content_template: '',
        sms_content_template: '',
        webhook_url: '',
        webhook_secret_key: '',
        webhook_include_pii: true,
        webhook_payload_template: '{}',
        webhook_headers: '{}',
        variables: '{}',
        sample_data: '{}',
        is_active: true,
      });
    }
  }, [template]);

  // AI Generation mutation
  const aiGenerationMutation = useMutation({
    mutationFn: async (prompt: string) => {
      const { data, error } = await supabase.functions.invoke('generate-template-with-ai', {
        body: {
          prompt,
          category: formData.category,
          channel: activeTab,
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.subject) {
        if (activeTab === 'email') {
          setFormData(prev => ({ ...prev, email_subject_template: data.subject }));
        }
      }
      if (data.content) {
        if (activeTab === 'email') {
          setFormData(prev => ({ ...prev, email_content_template: data.content }));
        } else if (activeTab === 'sms') {
          setFormData(prev => ({ ...prev, sms_content_template: data.content }));
        }
      }
      
      toast({
        title: "Success",
        description: `${activeTab.toUpperCase()} template generated successfully!`,
      });
      
      setShowAiGenerator(false);
      setAiPrompt('');
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to generate template",
        variant: "destructive",
      });
    }
  });

  const handleAiGenerate = async () => {
    if (!aiPrompt || !formData.category) {
      toast({
        title: "Error",
        description: "Please provide a prompt and select a category",
        variant: "destructive",
      });
      return;
    }

    await aiGenerationMutation.mutateAsync(aiPrompt);
  };

  // Insert variable helper
  const insertVariable = (variableKey: string, fieldName: string) => {
    const variableTag = `{{${variableKey}}}`;
    
    setFormData(prev => {
      const currentValue = prev[fieldName as keyof typeof prev] as string;
      return {
        ...prev,
        [fieldName]: currentValue + variableTag
      };
    });
  };

  // Scan for unknown variables
  const scanTemplateVariables = () => {
    const allContent = [
      formData.email_subject_template,
      formData.email_content_template,
      formData.sms_content_template
    ].join(' ');

    const variableMatches = allContent.match(/\{\{([^}]+)\}\}/g) || [];
    const usedVariables = variableMatches
      .map(match => match.replace(/\{\{|\}\}/g, '').trim())
      .filter(variable => {
        // Skip Handlebars block helpers (opening tags)
        if (variable.startsWith('#')) return false;
        // Skip Handlebars block closers
        if (variable.startsWith('/')) return false;
        // Skip standalone helper names
        if (['unless', 'if', 'each', 'with'].includes(variable)) return false;
        return true;
      })
      .map(variable => {
        // Extract actual variable from helper expressions (e.g., "unless booking.field" -> "booking.field")
        const helperMatch = variable.match(/^(?:unless|if|each|with)\s+(.+?)(?:\s|$)/);
        if (helperMatch) {
          return helperMatch[1];
        }
        return variable;
      });
    
    const knownVariableKeys = templateVariables.map(v => v.variable_key);
    const unknown = usedVariables.filter(v => !knownVariableKeys.includes(v));
    
    setUnknownVariables(Array.from(new Set(unknown)));
  };

  // Save mutation
  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      let variables, sample_data;
      
      try {
        variables = JSON.parse(data.variables);
      } catch (e) {
        throw new Error('Invalid JSON in variables field');
      }
      
      try {
        sample_data = JSON.parse(data.sample_data);
      } catch (e) {
        throw new Error('Invalid JSON in sample_data field');
      }

      const payload = {
        name: data.name,
        category: data.category,
        description: data.description || null,
        template_format: 'react_email',
        // New channel-specific fields
        email_subject_template: data.email_subject_template,
        email_content_template: data.email_content_template,
        sms_content_template: data.sms_content_template || null,
        // Standardized webhook fields
        webhook_url: data.webhook_url || null,
        webhook_secret_key: data.webhook_secret_key || null,
        webhook_include_pii: data.webhook_include_pii,
        // Deprecated fields for backward compatibility
        subject_template: data.email_subject_template,
        content_template: data.email_content_template,
        variables,
        sample_data,
        is_active: data.is_active,
      };

      if (template) {
        const { error } = await supabase
          .from('communication_templates')
          .update(payload)
          .eq('id', template.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('communication_templates')
          .insert([payload]);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: `Template ${template ? 'updated' : 'created'} successfully`,
      });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to save template",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  // Preview template with multi-mode support
  const handlePreview = async () => {
    setIsPreviewLoading(true);
    
    try {
      // Validate inputs based on mode
      if (previewDataMode === 'booking') {
        if (!selectedBookingId) {
          throw new Error('Please select a booking for real data preview');
        }
      } else {
        if (!formData.category) {
          throw new Error('Please select a category for sample data preview');
        }
      }

      // Validate template content
      if (!formData.email_subject_template && !formData.email_content_template && !formData.sms_content_template) {
        throw new Error('Please enter template content to preview');
      }

      // Build request body based on preview mode
      const requestBody: any = {
        action: 'preview',
        template: {
          email_subject_template: formData.email_subject_template,
          email_content_template: formData.email_content_template,
          sms_content_template: formData.sms_content_template,
        },
        channel: activeTab,
      };

      if (previewDataMode === 'booking') {
        // Real booking data mode
        requestBody.bookingId = selectedBookingId;
        console.log('Preview with real booking:', selectedBookingId);
      } else {
        // Sample data mode
        let sample_data;
        try {
          sample_data = JSON.parse(formData.sample_data);
        } catch (e) {
          throw new Error('Invalid JSON in sample data');
        }
        requestBody.category = formData.category;
        requestBody.sampleData = sample_data;
        console.log('Preview with sample data, category:', formData.category);
      }

      console.log('Sending preview request:', { 
        mode: previewDataMode, 
        hasBookingId: !!requestBody.bookingId,
        hasCategory: !!requestBody.category,
        channel: activeTab 
      });

      const { data, error } = await supabase.functions.invoke('centralized-event-orchestrator', {
        body: requestBody
      });

      if (error) throw error;

      console.log('Preview response received:', {
        success: data?.success,
        hasPreview: !!data?.preview,
        previewKeys: data?.preview ? Object.keys(data.preview) : [],
        hasHtml: !!data?.preview?.html,
        hasHtmlContent: !!data?.preview?.html_content
      });

      if (data?.success && data?.preview) {
        // Fix: Edge function returns 'html' not 'html_content'
        const htmlContent = data.preview.html || data.preview.html_content || data.preview.text || data.preview.text_content || '';
        console.log('Setting preview content, length:', htmlContent.length);
        setPreviewContent(htmlContent);
        setIsPreviewOpen(true);
        
        toast({
          title: "Preview Generated",
          description: previewDataMode === 'booking' 
            ? `Preview generated using real data from booking ${selectedBookingId.substring(0, 8)}...`
            : "Preview generated using sample data",
        });
        
        if (data.validationResult?.errors?.length > 0) {
          toast({
            title: "Validation Issues Found",
            description: `${data.validationResult.errors.join(', ')}`,
            variant: "default",
          });
        }
      } else {
        throw new Error(data?.error || 'Failed to generate preview');
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      toast({
        title: "Preview Error",
        description: error?.message || "Failed to generate preview",
        variant: "destructive",
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Character counter for SMS
  const smsCharCount = formData.sms_content_template.length;
  const smsWarning = smsCharCount > 160;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col">
        <div className="flex-1 overflow-y-auto px-1">
        <DialogHeader>
          <DialogTitle>
            {template ? 'Edit Template' : 'Create New Template'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {bookingCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          {/* Channel Readiness Badges */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Template readiness:</span>
            {formData.email_content_template && <Badge variant="default">Email ✓</Badge>}
            {formData.sms_content_template && <Badge variant="default">SMS ✓</Badge>}
            {formData.webhook_payload_template !== '{}' && <Badge variant="default">Webhook ✓</Badge>}
          </div>

          {/* AI Template Generator */}
          {!template && (
            <Card className="border-dashed border-primary/30 bg-primary/5">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">AI Template Generator</CardTitle>
                </div>
                <CardDescription>
                  Let AI generate a professional {activeTab} template based on your requirements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!showAiGenerator ? (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowAiGenerator(true)}
                    className="w-full"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate {activeTab.toUpperCase()} Template with AI
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ai_prompt">
                        Describe what this {activeTab} template should do
                      </Label>
                      <Textarea
                        id="ai_prompt"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder={
                          activeTab === 'email' 
                            ? "e.g., 'Send a booking confirmation email to customers with their booking details, collection address, and next steps'"
                            : activeTab === 'sms'
                            ? "e.g., 'Send a brief SMS notification when booking status changes with tracking link'"
                            : "e.g., 'Send booking update webhook with customer info, booking status, and addresses'"
                        }
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        onClick={handleAiGenerate}
                        disabled={aiGenerationMutation.isPending || !formData.category}
                        className="flex-1"
                      >
                        {aiGenerationMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate Template
                          </>
                        )}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setShowAiGenerator(false);
                          setAiPrompt('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    {!formData.category && (
                      <p className="text-sm text-muted-foreground">
                        Please select a category first to enable AI generation.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Channel Tabs */}
          <Card>
            <CardHeader>
              <CardTitle>Channel Templates</CardTitle>
              <CardDescription>
                Configure content for each channel. Templates are channel-ready - event flows will select which channels to use.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="email">
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </TabsTrigger>
                  <TabsTrigger value="sms">
                    <Phone className="h-4 w-4 mr-2" />
                    SMS
                  </TabsTrigger>
                  <TabsTrigger value="webhook">
                    <Code className="h-4 w-4 mr-2" />
                    Webhook
                  </TabsTrigger>
                </TabsList>

                {/* Email Tab */}
                <TabsContent value="email" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="email_subject">Email Subject</Label>
                      <Select onValueChange={(value) => insertVariable(value, 'email_subject_template')} value="">
                        <SelectTrigger className="w-48 h-8 text-xs">
                          <SelectValue placeholder="Insert variable" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-background border">
                          {Object.entries(groupedVariables).map(([category, variables]) => (
                            <div key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                {category.charAt(0).toUpperCase() + category.slice(1)}
                              </div>
                              {variables.map(variable => (
                                <SelectItem 
                                  key={variable.variable_key} 
                                  value={variable.variable_key}
                                  className="pl-6"
                                >
                                  {variable.display_name}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      id="email_subject"
                      value={formData.email_subject_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, email_subject_template: e.target.value }))}
                      onBlur={scanTemplateVariables}
                      placeholder="e.g., Booking Confirmed - {{booking_id}}"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="email_content">Email Content (HTML)</Label>
                      <Select onValueChange={(value) => insertVariable(value, 'email_content_template')} value="">
                        <SelectTrigger className="w-48 h-8 text-xs">
                          <SelectValue placeholder="Insert variable" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-background border">
                          {Object.entries(groupedVariables).map(([category, variables]) => (
                            <div key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                {category.charAt(0).toUpperCase() + category.slice(1)}
                              </div>
                              {variables.map(variable => (
                                <SelectItem 
                                  key={variable.variable_key} 
                                  value={variable.variable_key}
                                  className="pl-6"
                                >
                                  {variable.display_name}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      id="email_content"
                      value={formData.email_content_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, email_content_template: e.target.value }))}
                      onBlur={scanTemplateVariables}
                      placeholder="Email HTML content with variables..."
                      rows={15}
                      className="font-mono text-sm"
                    />
                  </div>

                  {/* Preview Mode Selector */}
                  <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                    <Label>Preview Mode</Label>
                    
                    <RadioGroup 
                      value={previewDataMode} 
                      onValueChange={(value) => setPreviewDataMode(value as 'sample' | 'booking')}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sample" id="sample" />
                        <Label htmlFor="sample" className="font-normal cursor-pointer">
                          Sample Data (Default test values)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="booking" id="booking" />
                        <Label htmlFor="booking" className="font-normal cursor-pointer">
                          Real Booking Data (Test with actual booking)
                        </Label>
                      </div>
                    </RadioGroup>
                    
                    {previewDataMode === 'booking' && (
                      <div className="space-y-2">
                        <Label htmlFor="bookingId">Select Booking</Label>
                        <Select 
                          value={selectedBookingId} 
                          onValueChange={setSelectedBookingId}
                          disabled={isLoadingBookings}
                        >
                          <SelectTrigger className="font-mono text-sm">
                            <SelectValue placeholder={isLoadingBookings ? "Loading bookings..." : "Select a recent booking"} />
                          </SelectTrigger>
                          <SelectContent className="bg-background z-50">
                            {recentBookings && recentBookings.length > 0 ? (
                              recentBookings.map((booking: any) => (
                                <SelectItem 
                                  key={booking.id} 
                                  value={booking.id} 
                                  className="font-mono text-xs cursor-pointer"
                                >
                                  <div className="flex flex-col py-1">
                                    <span className="font-semibold">
                                      {booking.id.substring(0, 8)}... • {booking.status}
                                    </span>
                                    <span className="text-muted-foreground text-xs">
                                      {booking.customer_name} • {new Date(booking.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="no-bookings" disabled>
                                {isLoadingBookings ? 'Loading bookings...' : 'No bookings available'}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground">
                          💡 Select a recent booking to preview the template with real data
                        </p>
                      </div>
                    )}
                  </div>

                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handlePreview}
                    disabled={isPreviewLoading || !formData.email_content_template}
                  >
                    {isPreviewLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating Preview...
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview Email
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* SMS Tab */}
                <TabsContent value="sms" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="sms_content">SMS Content</Label>
                      <Select onValueChange={(value) => insertVariable(value, 'sms_content_template')} value="">
                        <SelectTrigger className="w-48 h-8 text-xs">
                          <SelectValue placeholder="Insert variable" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-background border">
                          {Object.entries(groupedVariables).map(([category, variables]) => (
                            <div key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                {category.charAt(0).toUpperCase() + category.slice(1)}
                              </div>
                              {variables.map(variable => (
                                <SelectItem 
                                  key={variable.variable_key} 
                                  value={variable.variable_key}
                                  className="pl-6"
                                >
                                  {variable.display_name}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      id="sms_content"
                      value={formData.sms_content_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, sms_content_template: e.target.value }))}
                      onBlur={scanTemplateVariables}
                      placeholder="SMS text content (160 chars recommended)..."
                      rows={5}
                      maxLength={320}
                    />
                    <div className={`text-sm ${smsWarning ? 'text-yellow-600 dark:text-yellow-400' : 'text-muted-foreground'}`}>
                      {smsCharCount}/160 characters
                      {smsWarning && ' (may be split into multiple messages)'}
                    </div>
                  </div>
                </TabsContent>

                {/* Webhook Tab */}
                <TabsContent value="webhook" className="space-y-4 mt-4">
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      Standardized Webhook Payload
                    </h4>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Webhooks use a standardized payload structure that includes all booking, customer, and event data. 
                      The payload is automatically generated and signed with HMAC-SHA256 for security verification.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="webhook_url">Webhook URL *</Label>
                    <Input
                      id="webhook_url"
                      type="url"
                      value={formData.webhook_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, webhook_url: e.target.value }))}
                      placeholder="https://your-api.com/webhooks/booking-events"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      The endpoint URL where webhook payloads will be delivered
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="webhook_secret_key">Webhook Secret Key</Label>
                    <Input
                      id="webhook_secret_key"
                      type="password"
                      value={formData.webhook_secret_key}
                      onChange={(e) => setFormData(prev => ({ ...prev, webhook_secret_key: e.target.value }))}
                      placeholder="Leave empty to use organization default"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Secret key for HMAC-SHA256 signature generation. If not provided, the organization's default webhook secret will be used.
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 py-2">
                    <Switch
                      id="webhook_include_pii"
                      checked={formData.webhook_include_pii}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, webhook_include_pii: checked }))}
                    />
                    <div>
                      <Label htmlFor="webhook_include_pii" className="cursor-pointer">
                        Include Personal Information (PII)
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        When enabled, includes customer names, emails, phone numbers, and full addresses in the payload
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-semibold">Standardized Payload Structure</Label>
                      <Badge variant="secondary" className="text-xs">Read-Only</Badge>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-auto max-h-96">
                      <pre>{`{
  "event": {
    "id": "booking_uuid",
    "type": "booking_created | status_changed | quote_added | etc",
    "timestamp": "ISO8601_timestamp",
    "organization_id": "org_uuid"
  },
  "booking": {
    "id": "uuid",
    "reference_number": "REF-12345",
    "status": "pending | confirmed | etc",
    "collection_datetime": "ISO8601",
    "delivery_preference": "standard | express",
    "total_parcels_count": 1,
    "special_instructions": ${formData.webhook_include_pii ? '"PII: included if toggle enabled"' : '"PII: excluded"'}
  },
  "customer": ${formData.webhook_include_pii ? `{
    "id": "uuid",
    "name": "John Doe",
    "email": "john@example.com",
    "phone": "+1234567890"
  }` : `{
    "id": "uuid_only"
  }`},
  "collection": ${formData.webhook_include_pii ? `{
    "address": { "street": "...", "city": "...", ... },
    "contact": { "name": "...", "phone": "...", ... }
  }` : '{ /* excluded */ }'},
  "delivery": ${formData.webhook_include_pii ? `{
    "address": { "street": "...", "city": "...", ... },
    "contact": { "name": "...", "phone": "...", ... }
  }` : '{ /* excluded */ }'},
  "parcel": { "type": "...", "quantity": 1, ... },
  "quote": { "id": "...", "amount": 100.00, ... },
  "status_transition": { "from_status": "...", "to_status": "..." },
  "metadata": {
    "template_id": "uuid",
    "template_name": "${formData.name || 'template_name'}",
    "pii_included": ${formData.webhook_include_pii}
  }
}`}</pre>
                    </div>
                  </div>

                  <div className="space-y-2 pt-4 border-t">
                    <Label className="text-sm font-semibold">Security Headers</Label>
                    <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
                      <div><span className="text-primary">X-Webhook-Timestamp:</span> Unix timestamp in milliseconds</div>
                      <div><span className="text-primary">X-Webhook-Signature:</span> HMAC-SHA256 hex signature</div>
                      <div><span className="text-primary">X-Webhook-Event-Type:</span> Event type identifier</div>
                      <div><span className="text-primary">X-Webhook-Event-Id:</span> Event/Booking ID</div>
                      <div><span className="text-primary">X-Organization-Id:</span> Organization UUID</div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Signature validation: <code className="text-xs bg-muted px-1 py-0.5 rounded">HMAC-SHA256(timestamp + "." + JSON.stringify(payload), secret_key)</code>
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Unknown Variables Warning */}
          {unknownVariables.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-4 rounded">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Unknown Variables Detected
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                    <p>The following variables are not in the template variables database:</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {unknownVariables.map(varKey => (
                        <code key={varKey} className="px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-100 text-xs font-mono">
                          {`{{${varKey}}}`}
                        </code>
                      ))}
                    </div>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                      These variables will use placeholder sample data. Consider adding them to the template variables database for better consistency.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Variables and Sample Data */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="variables">Variables (JSON)</Label>
              <Textarea
                id="variables"
                value={formData.variables}
                onChange={(e) => setFormData(prev => ({ ...prev, variables: e.target.value }))}
                placeholder='{"variable_name": "description"}'
                rows={6}
                className="font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sample_data">Sample Data (JSON)</Label>
              <Textarea
                id="sample_data"
                value={formData.sample_data}
                onChange={(e) => setFormData(prev => ({ ...prev, sample_data: e.target.value }))}
                placeholder='{"variable_name": "sample value"}'
                rows={6}
                className="font-mono text-sm"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : (template ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </form>
        </div>
      </DialogContent>

      {/* Template Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-6xl h-[95vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle>Template Preview</DialogTitle>
              <div className="flex items-center gap-2">
                <Label htmlFor="preview-mode" className="text-sm">View:</Label>
                <Select value={previewDisplayMode} onValueChange={(value: 'desktop' | 'mobile') => setPreviewDisplayMode(value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desktop">Desktop</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <div className="bg-gray-50 p-4 rounded-md h-full flex flex-col">
              <h3 className="font-semibold mb-2 flex-shrink-0">Rendered Email Template:</h3>
              <div className="flex-1 flex justify-center items-start overflow-auto p-4">
                {previewDisplayMode === 'mobile' ? (
                  <div 
                    className="bg-white border-8 border-gray-800 rounded-[2rem] shadow-2xl overflow-hidden"
                    style={{ 
                      width: '375px',
                      height: '667px'
                    }}
                  >
                    <iframe
                      srcDoc={`
                        <!DOCTYPE html>
                        <html>
                          <head>
                            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
                            <style>
                              body { 
                                margin: 0; 
                                padding: 0;
                                overflow-x: hidden;
                              }
                            </style>
                          </head>
                          <body>${previewContent}</body>
                        </html>
                      `}
                      className="w-full h-full border-0"
                      title="Mobile Email Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                ) : (
                  <div className="bg-white border rounded shadow-sm w-full max-w-4xl overflow-auto">
                    <iframe
                      srcDoc={`
                        <!DOCTYPE html>
                        <html>
                          <head>
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <style>
                              body { 
                                margin: 0; 
                                padding: 20px;
                                display: flex;
                                justify-content: center;
                              }
                            </style>
                          </head>
                          <body>${previewContent}</body>
                        </html>
                      `}
                      className="w-full border-0"
                      style={{ height: '600px', minHeight: '600px' }}
                      title="Desktop Email Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
